// =====================================================
// PRICING RULE MODEL
// Stage 2: Participants & Pricing
// Business logic and helpers for pricing rules
// =====================================================

import { formatBs } from '@/lib/money';
import type {
  PricingRule,
  PricingCondition,
  ConditionType,
  ConditionOperator,
  PricingActionType,
  ParticipantType,
} from '@/types/pricing.types';

// ===================
// OPTION CATALOGS
// ===================

export const PARTICIPANT_TYPE_OPTIONS: { value: ParticipantType; label: string }[] = [
  { value: 'MINISTER', label: 'Ministro' },
  { value: 'DELEGATE', label: 'Delegado' },
  { value: 'GUEST', label: 'Invitado' },
];

/** Authority codes match mapAuthorityPositionToRoleCode() in the pricing engine */
export const AUTHORITY_ROLE_OPTIONS: { value: string; label: string }[] = [
  // District roles
  { value: 'SUPERINTENDENTE', label: 'Superintendente' },
  { value: 'VICE_SUPERINTENDENTE', label: 'Vice Superintendente' },
  { value: 'SECRETARIO_DISTRITO', label: 'Secretario (Distrito)' },
  { value: 'TESORERO_DISTRITO', label: 'Tesorero (Distrito)' },
  { value: 'VOCAL1_DISTRITO', label: 'Vocal 1 (Distrito)' },
  { value: 'VOCAL2_DISTRITO', label: 'Vocal 2 (Distrito)' },
  // Region roles
  { value: 'PRESBITERO', label: 'Presbítero' },
  { value: 'VICE_PRESBITERO', label: 'Vice Presbítero' },
  { value: 'SECRETARIO_REGION', label: 'Secretario (Región)' },
  { value: 'TESORERO_REGION', label: 'Tesorero (Región)' },
  { value: 'VOCAL1_REGION', label: 'Vocal 1 (Región)' },
  { value: 'VOCAL2_REGION', label: 'Vocal 2 (Región)' },
  // National roles
  { value: 'PRESIDENTE_NACIONAL', label: 'Presidente Nacional' },
  { value: 'VICE_PRESIDENTE_NACIONAL', label: 'Vice Presidente Nacional' },
  { value: 'SECRETARIO_NACIONAL', label: 'Secretario Nacional' },
  { value: 'TESORERO_NACIONAL', label: 'Tesorero Nacional' },
  { value: 'VOCAL1_NACIONAL', label: 'Vocal 1 (Nacional)' },
  { value: 'VOCAL2_NACIONAL', label: 'Vocal 2 (Nacional)' },
  // Other common roles (matched against the minister's free-text position)
  { value: 'PASTOR', label: 'Pastor' },
];

/** id → display name, to show readable conditions instead of UUIDs */
export interface ConditionLookups {
  degrees: Map<string, string>;
  districts: Map<string, string>;
  regions: Map<string, string>;
}

// ===================
// CONDITION HELPERS
// ===================

/**
 * Get human-readable label for condition type
 */
export function getConditionTypeLabel(type: ConditionType): string {
  const labels: Record<ConditionType, string> = {
    PARTICIPANT_TYPE: 'Tipo de participante',
    MINISTER_DEGREE: 'Grado ministerial',
    MINISTER_AGE: 'Edad',
    DISTRICT: 'Distrito',
    REGION: 'Región',
    ROLE: 'Cargo',
    REGISTERED_BEFORE: 'Inscrito hasta una fecha',
    PARTICIPANT_COUNT: 'Primeros inscritos',
    EARLY_BIRD: 'Inscripción temprana',
    GUEST_TYPE: 'Tipo de invitado',
  };
  return labels[type] || type;
}

/**
 * Get human-readable label for operator
 */
export function getOperatorLabel(operator: ConditionOperator): string {
  const labels: Record<ConditionOperator, string> = {
    EQUALS: 'es igual a',
    NOT_EQUALS: 'no es igual a',
    IN: 'es uno de',
    NOT_IN: 'no es uno de',
    GREATER_THAN: 'mayor que',
    LESS_THAN: 'menor que',
    GREATER_THAN_OR_EQUAL: 'mayor o igual a',
    LESS_THAN_OR_EQUAL: 'menor o igual a',
    BETWEEN: 'entre',
    CONTAINS: 'contiene',
    STARTS_WITH: 'empieza con',
    ENDS_WITH: 'termina con',
  };
  return labels[operator] || operator;
}

const SET_TYPES: ConditionType[] = ['PARTICIPANT_TYPE', 'MINISTER_DEGREE', 'DISTRICT', 'REGION', 'ROLE'];

export function isSetCondition(type: ConditionType): boolean {
  return SET_TYPES.includes(type);
}

/** Display name of one stored value (undefined when it can't be resolved) */
function resolveValueName(
  type: ConditionType,
  value: string,
  lookups?: ConditionLookups
): string | undefined {
  switch (type) {
    case 'PARTICIPANT_TYPE':
      return PARTICIPANT_TYPE_OPTIONS.find((o) => o.value === value)?.label;
    case 'ROLE':
      return AUTHORITY_ROLE_OPTIONS.find((o) => o.value === value)?.label;
    case 'MINISTER_DEGREE':
      return lookups?.degrees.get(value);
    case 'DISTRICT':
      return lookups?.districts.get(value);
    case 'REGION':
      return lookups?.regions.get(value);
    default:
      return value;
  }
}

function asArray(value: PricingCondition['value']): (string | number)[] {
  return Array.isArray(value) ? value : [value];
}

/**
 * True when a condition can never match: its values don't exist
 * (e.g. rules saved with typed names instead of ids by older versions).
 * Only decidable once the lookups are loaded.
 */
export function isConditionBroken(condition: PricingCondition, lookups?: ConditionLookups): boolean {
  if (condition.type === 'GUEST_TYPE') return true; // no source data
  if (!isSetCondition(condition.type)) {
    if (condition.type === 'REGISTERED_BEFORE') return !/^\d{4}-\d{2}-\d{2}/.test(String(condition.value));
    return false;
  }
  const values = asArray(condition.value).map(String);
  if (values.length === 0) return true;
  const needsLookup = ['MINISTER_DEGREE', 'DISTRICT', 'REGION'].includes(condition.type);
  if (needsLookup && !lookups) return false;
  return values.some((v) => resolveValueName(condition.type, v, lookups) === undefined);
}

const formatDate = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

/**
 * Format full condition for display (ids are resolved to names when lookups are given)
 */
export function formatCondition(condition: PricingCondition, lookups?: ConditionLookups): string {
  const { type, operator, value } = condition;
  const [a, b] = asArray(value);

  if (isSetCondition(type)) {
    const names = asArray(value).map((v) => resolveValueName(type, String(v), lookups) ?? String(v));
    const negative = operator === 'NOT_IN' || operator === 'NOT_EQUALS';
    return `${getConditionTypeLabel(type)} ${negative ? 'no es' : 'es'} ${names.join(' o ')}`;
  }

  switch (type) {
    case 'MINISTER_AGE':
      switch (operator) {
        case 'BETWEEN':
          return `Edad entre ${a} y ${b} años`;
        case 'GREATER_THAN_OR_EQUAL':
          return `Edad de ${a} años o más`;
        case 'LESS_THAN_OR_EQUAL':
          return `Edad hasta ${a} años`;
        case 'EQUALS':
          return `Edad de ${a} años`;
      }
      break;
    case 'REGISTERED_BEFORE':
      return `Inscrito hasta el ${formatDate(String(a))}`;
    case 'PARTICIPANT_COUNT':
      if (operator === 'LESS_THAN') return `Primeros ${a} inscritos`;
      break;
    case 'EARLY_BIRD':
      switch (operator) {
        case 'GREATER_THAN_OR_EQUAL':
          return `Inscripción con ${a} o más días de anticipación`;
        case 'LESS_THAN':
          return `Inscripción con menos de ${a} días de anticipación`;
        case 'BETWEEN':
          return `Inscripción entre ${a} y ${b} días antes del evento`;
      }
      break;
  }

  const valueStr = operator === 'BETWEEN' ? `${a} y ${b}` : asArray(value).join(', ');
  return `${getConditionTypeLabel(type)} ${getOperatorLabel(operator)} ${valueStr}`;
}

// ===================
// ACTION HELPERS
// ===================

const money = (n: number) => formatBs(n);

/**
 * Get human-readable label for action type
 */
export function getActionTypeLabel(type: PricingActionType): string {
  const labels: Record<PricingActionType, string> = {
    SET_PRICE: 'Pagan un precio fijo',
    PERCENTAGE_DISCOUNT: 'Descuento en %',
    FIXED_DISCOUNT: 'Descuento en Bs',
  };
  return labels[type] || type;
}

/**
 * Format action for display
 */
export function formatAction(actionType: PricingActionType, actionValue: number): string {
  switch (actionType) {
    case 'PERCENTAGE_DISCOUNT':
      return `Descuento del ${actionValue}%`;
    case 'FIXED_DISCOUNT':
      return `Descuento de ${money(actionValue)}`;
    case 'SET_PRICE':
      return `Pagan ${money(actionValue)}`;
    default:
      return `${actionType}: ${actionValue}`;
  }
}

/**
 * Get input placeholder for action value
 */
export function getActionValuePlaceholder(actionType: PricingActionType): string {
  switch (actionType) {
    case 'PERCENTAGE_DISCOUNT':
      return 'Ej: 20 (para 20%)';
    case 'FIXED_DISCOUNT':
      return 'Ej: 50';
    case 'SET_PRICE':
      return 'Ej: 150';
    default:
      return '';
  }
}

/**
 * Validate action value (`basePrice` lets us reject prices above the event price)
 */
export function validateActionValue(
  actionType: PricingActionType,
  value: number,
  basePrice?: number
): { valid: boolean; error?: string } {
  if (actionType === 'PERCENTAGE_DISCOUNT') {
    if (value <= 0 || value > 100) {
      return { valid: false, error: 'El porcentaje debe ser mayor a 0 y hasta 100' };
    }
  }

  if (actionType === 'FIXED_DISCOUNT' && value <= 0) {
    return { valid: false, error: 'El descuento debe ser mayor a 0' };
  }

  if (actionType === 'SET_PRICE') {
    if (value < 0) return { valid: false, error: 'El precio no puede ser negativo' };
    if (basePrice !== undefined && value > basePrice) {
      return {
        valid: false,
        error: `El precio fijo no puede superar el precio base del evento (${money(basePrice)})`,
      };
    }
  }

  return { valid: true };
}

// ===================
// RULE HELPERS
// ===================

/**
 * Get badge variant for rule status
 */
export function getRuleStatusBadgeVariant(
  isActive: boolean
): 'default' | 'secondary' | 'destructive' {
  return isActive ? 'default' : 'secondary';
}

/**
 * Get status label
 */
export function getRuleStatusLabel(isActive: boolean): string {
  return isActive ? 'Activa' : 'Inactiva';
}

/**
 * Calculate discount preview
 */
export function calculateDiscountPreview(
  basePrice: number,
  actionType: PricingActionType,
  actionValue: number
): { finalPrice: number; discountAmount: number; discountPercent: number } {
  let finalPrice = basePrice;
  let discountAmount = 0;

  switch (actionType) {
    case 'PERCENTAGE_DISCOUNT':
      discountAmount = (basePrice * actionValue) / 100;
      finalPrice = basePrice - discountAmount;
      break;
    case 'FIXED_DISCOUNT':
      discountAmount = actionValue;
      finalPrice = Math.max(0, basePrice - actionValue);
      break;
    case 'SET_PRICE':
      finalPrice = actionValue;
      discountAmount = basePrice - actionValue;
      break;
  }

  const discountPercent = basePrice > 0 ? (discountAmount / basePrice) * 100 : 0;

  return {
    finalPrice: Math.max(0, finalPrice),
    discountAmount: Math.max(0, discountAmount),
    discountPercent: Math.max(0, discountPercent),
  };
}

/**
 * Validate pricing rule
 */
export function validatePricingRuleForm(data: {
  name: string;
  actionType: PricingActionType | null;
  actionValue: number | null;
  basePrice?: number;
}): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  if (!data.name || data.name.trim().length === 0) {
    errors.name = 'El nombre es obligatorio';
  }

  if (!data.actionType) {
    errors.actionType = 'Elige qué se aplica a estos participantes';
  }

  if (data.actionValue === null || data.actionValue === undefined) {
    errors.actionValue = 'El valor es obligatorio';
  } else if (data.actionType) {
    const validation = validateActionValue(data.actionType, data.actionValue, data.basePrice);
    if (!validation.valid && validation.error) {
      errors.actionValue = validation.error;
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Sort rules by priority
 */
export function sortRulesByPriority(rules: PricingRule[]): PricingRule[] {
  return [...rules].sort((a, b) => a.priority - b.priority);
}

/**
 * Get summary of pricing rule
 */
export function getPricingRuleSummary(rule: PricingRule, lookups?: ConditionLookups): string {
  const who =
    rule.conditions.length === 0
      ? 'Todos los participantes'
      : rule.conditions.map((c) => formatCondition(c, lookups)).join(' y ');

  return `${who} → ${formatAction(rule.action_type, rule.action_value)}`;
}
